import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sendForm = async (payload) => {
  const { data } = await axios.post(`${API}/admin/create-listing/send`, payload);
  toast.success(`Create-listing form sent to ${payload.email}`);
  return data;
};

// Funnel Inbox: one-click send of the create-listing form to a listing request.
export const SendListingFormButton = ({ lead, onSent }) => {
  const [busy, setBusy] = useState(false);
  const sent = lead.create_listing_sent_at;
  const go = async () => {
    if (sent && !window.confirm('The form was already sent to this lead. Send it again?')) return;
    setBusy(true);
    try {
      await sendForm({ email: lead.email, name: lead.name || '', organisation: lead.organisation || '', lead_id: lead.id });
      onSent && onSent(lead.id, { create_listing_sent_at: new Date().toISOString() });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send form'); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={busy} data-testid={`lead-send-listing-form-${lead.id}`}
      title="Email this person a private link to the self-serve Create a listing form"
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] disabled:opacity-60 transition-colors">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
      {sent ? 'Resend listing form' : 'Send create-listing form'}
    </button>
  );
};

// Settings > Admin Tools: send the form to any email + recent sends.
export const SendListingFormPanel = () => {
  const [f, setF] = useState({ email: '', name: '', organisation: '' });
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/admin/create-listing/invites`).then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.email.includes('@')) { toast.error('Enter a valid email'); return; }
    setBusy(true);
    try { await sendForm(f); setF({ email: '', name: '', organisation: '' }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not send form'); }
    finally { setBusy(false); }
  };
  const publicLink = `${window.location.origin}/create-listing`;
  const inp = 'bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-[#ffffff] placeholder:text-slate-500';
  return (
    <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700" data-testid="send-listing-form-panel">
      <h3 className="text-sm font-semibold text-[#ffffff] flex items-center gap-2"><Send className="w-4 h-4 text-[#00A79D]" /> Send "Create a listing" form</h3>
      <p className="text-xs text-slate-400">Emails a supplier a private link to the self-serve listing form (company, product, team size, plan & payment). Public page:{' '}
        <button type="button" onClick={() => { navigator.clipboard.writeText(publicLink); toast.success('Link copied'); }} className="text-[#00A79D] inline-flex items-center gap-1" data-testid="copy-public-listing-link">{publicLink} <Copy className="w-3 h-3" /></button>
      </p>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-2">
        <input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email *" className={inp} data-testid="slf-email" />
        <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={inp} data-testid="slf-name" />
        <input value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} placeholder="Company" className={inp} data-testid="slf-organisation" />
        <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] text-sm font-semibold rounded-md px-3 py-2 disabled:opacity-60" data-testid="slf-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send form
        </button>
      </form>
      {items.length > 0 && (
        <div className="space-y-1" data-testid="slf-recent">
          <p className="text-xs font-semibold text-slate-400">Recently sent</p>
          {items.slice(0, 8).map((i) => (
            <div key={i.id} className="flex items-center justify-between text-xs text-slate-300 border-b border-slate-700/60 py-1.5">
              <span>{i.name || i.email}{i.organisation ? ` · ${i.organisation}` : ''} <span className="text-slate-500">{i.email}</span></span>
              <span className="capitalize text-slate-400" data-testid={`slf-status-${i.id}`}>{i.status}{i.opened_at && i.status === 'sent' ? ' · opened' : ''} · {new Date(i.sent_at).toLocaleDateString('en-GB')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
